// Phase-3 Slice 34 — tab rename notice banner.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 34 + § NAV1 (amended
// 2026-08-23).
//
// Non-modal banner rendered above the Monaco editor after a rename touched
// this tab. Two flavours, driven by `notice.outcome`:
//
//   rewritten — the references WERE updated in place; the banner says how many
//               and reminds the user that Ctrl+Z restores the buffer.
//   stale     — references were found but deliberately not rewritten (buffer
//               failed to parse, or occurrences the resolver could not prove).
//               Warning-flavoured, because the buffer now names something that
//               no longer exists. Never a silent skip.
//
// Dismissible — clears just this banner from the store.

import { useCallback } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';

import { Button } from '../../../components/ui/button';

import type { QualifiedName, TabCourtesyNotice } from '../types';

import { useRenameTabNoticeStore } from './renameTabNoticeStore';

type Props = {
  readonly notice: TabCourtesyNotice;
};

export function RenameTabNotice({ notice }: Props) {
  const dismissNotice = useRenameTabNoticeStore((s) => s.dismissNotice);
  const onDismiss = useCallback(() => {
    dismissNotice(notice.tabId, notice.oldName, notice.newName);
  }, [dismissNotice, notice]);

  const oldFq = formatQualifiedName(notice.oldName);
  const newFq = formatQualifiedName(notice.newName);
  const rewritten = notice.outcome === 'rewritten';
  // A rewrite is only "clean" when EVERY occurrence went in and the edit is on
  // the tab's undo stack. A partial rewrite, or one the store fallback applied
  // (which discards undo), keeps the warning flavour so the banner never
  // over-promises.
  const clean = rewritten && (notice.skippedCount ?? 0) === 0 && notice.undoable !== false;

  return (
    <div
      className={
        clean
          ? 'flex items-start gap-3 px-4 py-2 bg-sky-50 dark:bg-sky-950/30 border-b border-sky-200 dark:border-sky-800'
          : 'flex items-start gap-3 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800'
      }
      data-testid="rename-tab-notice"
      data-tab-id={notice.tabId}
      data-outcome={rewritten ? 'rewritten' : 'stale'}
    >
      {clean ? (
        <Info className="h-4 w-4 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
      )}

      <div className="flex-1 min-w-0">
        <p className={clean ? 'text-sm text-sky-800 dark:text-sky-200' : 'text-sm text-amber-800 dark:text-amber-200'}>
          <span className="font-mono font-medium">{oldFq}</span>
          {' was renamed to '}
          <span className="font-mono font-medium">{newFq}</span>
          {outcomeSentence(notice)}
        </p>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={onDismiss}
        className="h-6 w-6 p-0 flex-shrink-0"
        title="Dismiss"
        data-testid="rename-tab-notice-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/** What actually happened to this buffer, in one sentence per fact. */
function outcomeSentence(notice: TabCourtesyNotice): string {
  if (notice.outcome !== 'rewritten') {
    return ' — this buffer still references the old name and could not be updated automatically.';
  }
  const updated = ` — updated ${referenceLabel(notice.rewrittenCount ?? notice.references.length)} in this tab.`;
  const skipped =
    (notice.skippedCount ?? 0) > 0
      ? ` ${referenceLabel(notice.skippedCount!)} could not be updated automatically — check this buffer by hand.`
      : '';
  const undo =
    notice.undoable === false
      ? " This tab's undo history was reset, so Ctrl+Z will not restore it."
      : ' Undo with Ctrl+Z.';
  return `${updated}${skipped}${undo}`;
}

function referenceLabel(count: number): string {
  return count === 1 ? '1 reference' : `${count} references`;
}

function formatQualifiedName(qn: QualifiedName): string {
  const parts: string[] = [];
  if (qn.schema) parts.push(qn.schema);
  parts.push(qn.name);
  return parts.join('.');
}
